// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  ThumbUp,
  ThumbDown,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import VoiceDetailModal from '../VoiceDetailModal';

const RecordsTable = ({
  data,
  loading,
  onPaginationChange,
  onSortChange,
  onListRefreshSuggested,
}) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Language code to display name mapping
  const getLanguageDisplayName = (languageCode) => {
    const languageMap = {
      'en': 'English',
      'th': 'Thai', 
      'zh': 'Chinese',
      'ar': 'Arabic',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ru': 'Russian',
      'yue': 'Cantonese',
      'vi': 'Vietnamese',
      'tl': 'Tagalog',
      'id': 'Bahasa Indonesia',
      'other': 'Other'
    };
    return languageMap[languageCode] || languageCode;
  };

  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  const { records, pagination } = data;

  const handleSort = (property) => {
    const isAsc = pagination.sort_by === property && pagination.sort_order === 'asc';
    onSortChange(property, isAsc ? 'desc' : 'asc');
  };

  const handleChangePage = (event, newPage) => {
    onPaginationChange({
      ...pagination,
      page: newPage + 1 // Convert to 1-based page
    });
  };

  const handleChangeRowsPerPage = (event) => {
    onPaginationChange({
      ...pagination,
      page: 1,
      limit: parseInt(event.target.value, 10)
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSentimentIcon = (sentiment) => {
    return sentiment === 'positive' ? (
      <ThumbUp sx={{ color: 'success.main', fontSize: 20 }} />
    ) : (
      <ThumbDown sx={{ color: 'error.main', fontSize: 20 }} />
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'error';
  };

  const handleViewAnalysis = (record) => {
    // Open voice analysis modal with default Analysis tab
    setSelectedRecord(record);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        QA Records
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Detailed view of QA analysis results
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={pagination.sort_by === 'qa_criteria'}
                  direction={pagination.sort_by === 'qa_criteria' ? pagination.sort_order : 'asc'}
                  onClick={() => handleSort('qa_criteria')}
                >
                  QA Criteria
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={pagination.sort_by === 'language'}
                  direction={pagination.sort_by === 'language' ? pagination.sort_order : 'asc'}
                  onClick={() => handleSort('language')}
                >
                  Language
                </TableSortLabel>
              </TableCell>
              <TableCell>
                Sentiment / Qualification
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={pagination.sort_by === 'score'}
                  direction={pagination.sort_by === 'score' ? pagination.sort_order : 'asc'}
                  onClick={() => handleSort('score')}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={pagination.sort_by === 'date'}
                  direction={pagination.sort_by === 'date' ? pagination.sort_order : 'asc'}
                  onClick={() => handleSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading records...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {record.qa_criteria}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getLanguageDisplayName(record.language)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {record.criteria_type === 'boolean' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {record.qualification === 'Yes' ? (
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                        )}
                        <Typography
                          variant="body2"
                          color={record.qualification === 'Yes' ? 'success.main' : 'error.main'}
                        >
                          {record.qualification === 'Yes' ? 'Qualified' : 'Not qualified'}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getSentimentIcon(record.sentiment)}
                        <Typography
                          variant="body2"
                          color={record.sentiment === 'positive' ? 'success.main' : 'error.main'}
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {record.sentiment}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {record.criteria_type === 'boolean' ? (
                      <Typography variant="body2" color="text.secondary">
                        {record.total_items != null && record.total_items > 0
                          ? `${record.qualified_count ?? 0}/${record.total_items} items qualified`
                          : `${record.qualified_count ?? 0} items qualified`}
                      </Typography>
                    ) : (
                      <Chip
                        label={`${record.score}/100`}
                        size="small"
                        color={getScoreColor(record.score)}
                        variant="filled"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(record.date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Voice Analysis">
                      <IconButton
                        size="small"
                        onClick={() => handleViewAnalysis(record)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total_records}
        page={pagination.current_page - 1} // Convert to 0-based page
        onPageChange={handleChangePage}
        rowsPerPage={pagination.records_per_page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
        labelRowsPerPage="Records per page:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        showFirstButton
        showLastButton
      />

      {/* Voice Detail Modal */}
      {selectedRecord && (
        <VoiceDetailModal
          open={modalOpen}
          onClose={handleCloseModal}
          onListRefreshSuggested={onListRefreshSuggested}
          fileId={selectedRecord.id}
          fileName={`${selectedRecord.qa_criteria} - ${getLanguageDisplayName(selectedRecord.language)}`}
        />
      )}
    </Box>
  );
};

export default RecordsTable;
